import { Injectable, OnDestroy, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, Subject, of, throwError } from 'rxjs';
import { tap, map, takeUntil, retryWhen, delay, concatMap } from 'rxjs/operators';
import { Router } from '@angular/router';
// model
import { HttpHeaderService } from 'src/app/shared/service/http-header.service';
import { SearchService } from 'src/app/shared/service/search/search.service';
import { DetailPageInfo } from '../../model/detail-page-info.model';
import { SearchPage } from '../../model/search-page.model';
import { RequestManagerService } from 'src/app/shared/service/request-manager.service';

@Injectable({
  providedIn: 'root'
})
export class SearchWorkerProjectService extends SearchService implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  // サービス毎に初期値を設定する
  initPage = { page: 1 };
  initPerPage = { perPage: 10 };
  selfServiceName = 'SearchWorkerProjectService';

  constructor(
    header: HttpHeaderService, 
    http: HttpClient,
    requestManager: RequestManagerService,
    private router: Router,
    injector: Injector
  ) {
    super(header, http, requestManager, injector);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  doSearch(id: string, detailPageInfo?: DetailPageInfo): Observable<any> {
    // 前のリクエストをキャンセル
    this.requestManager.cancelRequest('search');
    this.currentSearchRequest$.next();
    
    this.setSearchPage(new SearchPage({ page: 1 }));
    detailPageInfo = new DetailPageInfo(detailPageInfo);
    
    const searchParams = this.getSearchParameters(detailPageInfo.getParams());
    
    const url = 'api/v1/workers/' + id + '/projects';
    
    return super.requestPost(url, searchParams).pipe(
      takeUntil(this.currentSearchRequest$), // リクエストキャンセル用
      tap({
        next: (response) => {
          // 成功時のログ出力を削減
        },
        error: (error) => {
          // エラー時のログ出力を削減（重要なエラーのみ出力）
          if (error.status === 401) {
            console.error('Authentication error:', error.status);
          }
        }
      })
    );
  }

  showWorkerMail(id: string): Observable<any> {
    const url = 'api/v1/workers/' + id;
    return super.requestGet(url).pipe(
      // リトライ機能を追加（2回までリトライ）
      retryWhen(errors => 
        errors.pipe(
          concatMap((error, index) => {
            // 404エラーまたは500エラー（validate_table_name_for_userエラー）の場合のみリトライ
            if ((error.status === 404 || error.status === 500) && index < 2) {
              console.warn(`ワーカーメール取得が失敗しました。リトライ ${index + 1}/2:`, error);
              return of(error).pipe(delay(1000)); // 1秒待機してリトライ
            }
            // 2回失敗した場合、または対象外エラーの場合はエラーを投げる
            if ((error.status === 404 || error.status === 500) && index >= 2) {
              console.error('ワーカーメール取得が2回失敗しました。エラーメッセージを表示します。');
              this.showErrorToast();
            }
            return throwError(error);
          })
        )
      )
    );
  }

  showProjectMail(id: string): Observable<any> {
    const url = 'api/v1/projects/' + id;
    return super.requestGet(url);
  }

  getSearchParameters(detailPageInfo?: object) {
    const condition = this.getSearchCondition().getParams();
    const count = this.getSearchCount().getParams();
    const page = this.getSearchPage().getParams();
    const searchTags = { search_tag_ids: this.getSearchTagIds() };
    return Object.assign(condition, count, page, searchTags, detailPageInfo);
  }

  getTransitionPage(id: string, direction: string): Observable<any> {
    const params = { id: id, direction: direction };
    return super.requestGet('api/v1/workers/page_transition', params);
  }

  transitionPage(id: string, direction: string): void {
    let observable: Observable<string>;
    if (sessionStorage.workerIds) {
      observable = new Observable(observer => {
        observer.next(this.getNextmailIdFromSession(id, direction));
      });
    } else {
      observable = this.getTransitionPage(id, direction).pipe(
        map(res => res.body.worker.id)
      );
    }
    const transitionSub = observable.subscribe(mailId => {
      if (mailId) {
        this.router.navigate(['/matching/workers/' + mailId]);
      }
    });
    this.subscriptions.add(transitionSub);
  }

  private getNextmailIdFromSession(id: string, direction: string): string {
    let nexttMailId: string;
    const workerIds = sessionStorage.workerIds.split(',');
    const indexOfCurrentMail = workerIds.indexOf(id);
    if (direction === 'next') {
      nexttMailId = workerIds[indexOfCurrentMail + 1];
    } else {
      nexttMailId = workerIds[indexOfCurrentMail - 1];
    }
    return nexttMailId;
  }
}
